import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useManifest } from '../project/project.hooks';
import {
    OtaIntegrationsService,
    OtaName,
    ProductVariantsService,
} from '../../generated';
import { CustomQueryOptions, CustomMutationOptions } from '../common.types';
import {
    otaDtoToOta,
    productVariantDtoToProductVariant,
    productVariantToProductVariantDto,
} from './ota.converters';
import { Ota, ProductVariant, ProductVariantRequest } from './ota.types';

const activatedOtasCacheKey = 'activatedOtas';
export const useOtas = (options: CustomQueryOptions<any> = {}) => {
    const { data, isLoading: isManifestLoading } = useManifest();
    const { data: activatedOtas, ...other } = useQuery({
        queryKey: [activatedOtasCacheKey],
        queryFn: async () => {
            const response = await OtaIntegrationsService.getActivatedOtas({});

            if (!response.data) throw new Error('Failed');

            return response.data.items;
        },
        ...options,
    });

    const parsedData = useMemo(() => {
        return activatedOtas && data?.integrations?.otas
            ? (data.integrations.otas.map((ota: any) => ({
                  ...otaDtoToOta(ota),
                  enabled: activatedOtas
                      ? !!activatedOtas.find(
                            ({ name }: any) => name === ota.name
                        )
                      : false,
              })) as Ota[])
            : undefined;
    }, [activatedOtas, data?.integrations?.otas]);

    return {
        ...other,
        data: parsedData,
        isLoading: other.isLoading || isManifestLoading,
    };
};

export const useUpdateOtaStatus = (
    options: CustomMutationOptions<
        {
            id: OtaName;
            activate?: boolean;
        },
        any
    > = {}
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, activate }) => {
            const response = await OtaIntegrationsService.updateOtaStatus({
                path: { ota_name: id },
                query: activate !== undefined ? { activate } : undefined,
            });
            if (!response.data) throw new Error('Failed to update OTA status');
            return response.data;
        },
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries([activatedOtasCacheKey]);
            if (options.onSuccess) options.onSuccess(data, variables, context);
        },
    });
};

const productVariantsCacheKey = 'productVariants';
export const useProductVariants = (
    otaName?: OtaName,
    options: CustomQueryOptions<ProductVariant[]> = {}
) => {
    return useQuery({
        queryKey: [productVariantsCacheKey, otaName],
        enabled: !!otaName,
        queryFn: async () => {
            const response = await ProductVariantsService.getOtaProductVariants(
                {
                    query: {
                        integrations: otaName ? [otaName] : undefined,
                    },
                }
            );
            if (!response.data?.items)
                throw new Error('Failed to fetch product variants');
            return response.data.items.map(productVariant =>
                productVariantDtoToProductVariant(productVariant)
            );
        },
        ...options,
    });
};

export const useCreateProductVariant = (
    options: CustomMutationOptions<ProductVariantRequest, any> = {}
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async product => {
            const response = await ProductVariantsService.createProductVariant({
                body: productVariantToProductVariantDto(product),
            });
            if (!response.data) throw new Error('Failed to update OTA status');
            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            queryClient.invalidateQueries([
                productVariantsCacheKey,
                args[1].integration.name,
            ]);
            if (options.onSuccess) options.onSuccess(...args);
        },
    });
};

export const useOtaInfo = (otaId?: OtaName) => {
    const { data } = useManifest();

    const ota = useMemo(() => {
        const otaDto = data?.integrations?.otas?.find(
            (o: any) => o.name === otaId
        );
        return otaDto ? otaDtoToOta(otaDto) : undefined;
    }, [data]);

    return { ota };
};

export const useDeleteProductVariant = (
    options: CustomMutationOptions<{ id: string }, any> = {}
) => {
    return useMutation({
        mutationFn: async ({ id }) => {
            await ProductVariantsService.deleteOtaProductVariant({
                path: { product_variant_id: id },
            });
        },
        ...options,
    });
};
